import { AnalyticsEventName } from '@dupe/common/constants'
import truncate from 'lodash/truncate'
import { useEffect, useRef, useState } from 'react'
import Button from '~/src/components/Button'
import OnboardingTitle from '~/src/components/onboarding/OnboardingTitle'
import useAnalytics from '~/src/hooks/useAnalytics'
import useUpdateUserMetadata from '~/src/hooks/useUpdateUserMetadata'
import {
  OnboardingStep,
  useOnboardingActions,
  useOnboardingData,
  useOnboardingStore,
} from '~/src/stores/useOnboardingStore'

const mainOptions = [
  { key: 'save-money', value: 'Save money' },
  { key: 'save-time-finding-products', value: 'Save time finding products' },
  { key: 'discover-what-to-buy-next', value: 'Discover what to buy next' },
  { key: 'explore-styles', value: 'Explore styles' },
]

const otherOption = { key: 'other', value: 'Other' }

const JobToBeDoneStep: React.FC = () => {
  const analytics = useAnalytics()
  const jobToBeDone = useOnboardingStore(
    (state) => state.data.onboarding_data?.jobToBeDone
  )

  const [processing, setProcessing] = useState(false)
  const [selectedIntent, setSelectedIntent] = useState<string | null>(null)
  const [isOtherSelected, setIsOtherSelected] = useState(false)
  const [customIntent, setCustomIntent] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const [shuffledOptions] = useState(() => [
    ...mainOptions.sort(() => Math.random() - 0.5),
    otherOption,
  ])

  const nextStep = useOnboardingActions().nextStep
  const markStepComplete = useOnboardingActions().markStepComplete
  const setJobToBeDone = useOnboardingActions().setJobToBeDone
  const onboardingData = useOnboardingData()

  const { mutateAsync: updateUserMetadata } = useUpdateUserMetadata()

  const handleContinue = async (selectedIntent: string) => {
    if (processing) return

    setProcessing(true)
    setSelectedIntent(selectedIntent)

    const finalIntent =
      selectedIntent === 'other' && customIntent.trim()
        ? `other:${truncate(customIntent.trim(), { length: 250 })}`
        : selectedIntent

    // Track analytics
    analytics.track(AnalyticsEventName.AuthModalOnboardingSubmit, {
      step: 'job-to-be-done',
      selectedIntent: finalIntent,
    })

    // Save to store
    setJobToBeDone(finalIntent)

    // Save to database
    try {
      await updateUserMetadata({
        key: 'onboarding_data',
        value: {
          ...onboardingData,
          jobToBeDone: finalIntent,
        },
      })
    } catch (error) {
      console.error('Failed to update job to be done preference:', error)
    }

    markStepComplete(OnboardingStep.JOB_TO_BE_DONE)
    nextStep(OnboardingStep.DUPE_VALUE_PROP_TWO)
  }

  const handleSelectOption = (option: string) => {
    if (option === 'other') {
      setIsOtherSelected(true)
      setTimeout(() => {
        inputRef.current?.focus()
      }, 0)
    } else {
      setIsOtherSelected(false)
      // Clear the custom intent when a non-Other option is selected
      setCustomIntent('')
      handleContinue(option)
    }
  }

  const handleSubmitCustom = () => {
    if (customIntent.trim()) {
      handleContinue('other')
    }
  }

  const handleCustomInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCustomIntent(e.target.value)
    // Ensure isOtherSelected remains true while typing
    if (!isOtherSelected) {
      setIsOtherSelected(true)
    }
  }

  // Initialize with stored data if it exists
  useEffect(() => {
    if (jobToBeDone) {
      if (jobToBeDone.startsWith('other:')) {
        // Handle "Other" selection
        const storedCustomIntent = jobToBeDone.substring(6) // Remove 'other:' prefix
        setIsOtherSelected(true)
        setCustomIntent(storedCustomIntent)
        setSelectedIntent('other')
      } else {
        // Handle predefined option selection
        setSelectedIntent(jobToBeDone)
        // Ensure custom intent is clear when a predefined option is selected
        setCustomIntent('')
      }
    }
  }, [jobToBeDone])

  // Focus the input when "Other" is selected
  useEffect(() => {
    if (isOtherSelected && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isOtherSelected])

  return (
    <div className='space-y-4 md:space-y-10'>
      <OnboardingTitle
        title="What's the most important job you want Dupe to do for you right now?"
        description='We will do all of these but our AI needs to understand what to prioritize for you'
      />
      <div className='mx-auto max-w-sm space-y-2 md:space-y-3'>
        {shuffledOptions.map((option) => {
          // If this is the "other" option and it's selected, show an input field instead
          if (option.key === 'other' && isOtherSelected) {
            return (
              <div key={option.key} className='space-y-3'>
                <Button
                  variant={
                    selectedIntent === 'other' && isOtherSelected
                      ? 'primary'
                      : 'outline'
                  }
                  as='div'
                  rounded
                  fullWidth
                  className='text-left'
                >
                  <input
                    ref={inputRef}
                    type='text'
                    value={customIntent}
                    maxLength={250}
                    onChange={handleCustomInputChange}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleSubmitCustom()
                      }
                    }}
                    disabled={processing}
                    className='w-full !bg-transparent text-center font-medium'
                  />
                </Button>
                {customIntent.trim() && (
                  <Button
                    variant='primary'
                    fullWidth
                    onClick={handleSubmitCustom}
                    disabled={processing}
                  >
                    Submit
                  </Button>
                )}
              </div>
            )
          }

          return (
            <Button
              key={option.key}
              fullWidth
              disabled={processing && option.key !== selectedIntent}
              size='lg'
              rounded
              variant={option.key === selectedIntent ? 'primary' : 'outline'}
              className='font-medium'
              onClick={() => {
                handleSelectOption(option.key)
              }}
            >
              {option.value}
            </Button>
          )
        })}
      </div>
    </div>
  )
}

export default JobToBeDoneStep
